/**
 * resourceCache — caché en memoria con *stale-while-revalidate* para las
 * listas de /admin.
 *
 * ── Por qué existe ─────────────────────────────────────────────────────────
 * Cada pantalla de administración pedía sus datos en `useEffect` al montar,
 * sin caché y sin deduplicación. Medido contra producción (ago 2026):
 *
 *   - la consulta SQL de /api/fleet/vehicles tarda 3–5 ms y la de
 *     /api/fleet/gps-devices 3 ms, con 37 filas y 23–30 kB de JSON;
 *   - el viaje de red desde Colombia a la región `dfw` cuesta 110–145 ms por
 *     petición sobre una conexión ya abierta, y ~440 ms si hay que abrirla.
 *
 * O sea: el servidor no era el cuello. Lo que se percibía como lentitud era
 * que ir de Vehículos a Dispositivos y volver repetía DOS viajes de red
 * (la lista + los grupos) cada vez, escondiendo la tabla tras un «Cargando…»
 * aunque los mismos datos se hubieran traído tres segundos antes.
 *
 * Con esta caché, volver a una pantalla ya visitada pinta la tabla en el acto
 * y, si el dato pasó su TTL, se revalida por detrás sin tapar nada.
 *
 * ── Modelo ─────────────────────────────────────────────────────────────────
 * Una entrada por clave (`fleet:vehicles`, `fleet:groups`, …):
 *
 *   data       último valor bueno; se conserva aunque una revalidación falle
 *   error      error de la ÚLTIMA carga (null si fue bien)
 *   fetchedAt  ms epoch del último intento — 0 = nunca / invalidada
 *   promise    carga en vuelo; deduplica montajes simultáneos
 *   version    contador que cambia en cada notificación (deps de efectos)
 *
 * No es react-query: es lo mínimo que resuelve el problema sin sumar una
 * dependencia al bundle ni al `node_modules` compartido entre worktrees.
 */

/** @type {Map<string, object>} */
const cache = new Map();

function ensure(key) {
  let entry = cache.get(key);
  if (!entry) {
    entry = {
      key,
      data: undefined,
      error: null,
      fetchedAt: 0,
      promise: null,
      version: 0,
      listeners: new Set(),
    };
    cache.set(key, entry);
  }
  return entry;
}

function notify(entry) {
  entry.version += 1;
  // Copia defensiva: un listener puede desuscribirse durante el recorrido.
  for (const listener of [...entry.listeners]) listener();
}

/**
 * Lee la entrada sin provocar carga alguna. `undefined` si nunca se tocó.
 * @param {string} key
 */
export function peekResource(key) {
  return cache.get(key);
}

/**
 * Suscribe un listener a los cambios de una clave.
 * @param {string} key
 * @param {() => void} listener
 * @returns {() => void} desuscriptor
 */
export function subscribeResource(key, listener) {
  const entry = ensure(key);
  entry.listeners.add(listener);
  return () => {
    entry.listeners.delete(listener);
  };
}

/**
 * ¿Hace falta ir a la red? True si nunca se cargó, si se invalidó o si el TTL
 * venció.
 * @param {string} key
 * @param {number} ttlMs
 */
export function isResourceStale(key, ttlMs) {
  const entry = cache.get(key);
  if (!entry || entry.fetchedAt === 0) return true;
  // `>=` y no `>`: con TTL 0 —«este dato no vale para nada, tráelo otra vez»—
  // un `>` estricto devolvía «fresco» si la carga había terminado en el mismo
  // milisegundo, que es lo normal con datos de mentira en un test.
  return Date.now() - entry.fetchedAt >= ttlMs;
}

/**
 * Carga (o reutiliza la carga en vuelo) de una clave.
 *
 * `fetchedAt` se sella TAMBIÉN cuando falla: si solo se sellara en el camino
 * feliz, la entrada quedaría eternamente stale y el efecto que la observa
 * reintentaría en bucle contra un servidor caído.
 *
 * @param {string} key
 * @param {() => Promise<any>} fetcher
 * @param {{force?: boolean}} [opts] — `force` ignora la carga en vuelo y la
 *   respuesta de la vieja se descarta (gana la última que se lanzó).
 * @returns {Promise<any>}
 */
export function loadResource(key, fetcher, { force = false } = {}) {
  const entry = ensure(key);
  if (entry.promise && !force) return entry.promise;

  const promise = Promise.resolve()
    .then(fetcher)
    .then(
      (data) => {
        if (entry.promise !== promise) return data; // una recarga más nueva manda
        entry.data = data;
        entry.error = null;
        entry.fetchedAt = Date.now();
        entry.promise = null;
        notify(entry);
        return data;
      },
      (err) => {
        if (entry.promise === promise) {
          entry.error = err;
          entry.fetchedAt = Date.now();
          entry.promise = null;
          notify(entry);
        }
        throw err;
      },
    );

  entry.promise = promise;
  notify(entry); // para que los consumidores pinten el estado «revalidando»
  return promise;
}

/**
 * Escribe un valor a mano (respuesta de un POST/PUT, actualización optimista)
 * y lo marca como recién traído.
 * @param {string} key
 * @param {any | ((prev:any) => any)} next
 */
export function mutateResource(key, next) {
  const entry = ensure(key);
  entry.data = typeof next === "function" ? next(entry.data) : next;
  entry.error = null;
  entry.fetchedAt = Date.now();
  // Soltar la carga en vuelo: lo que se acaba de escribir es MÁS NUEVO que la
  // respuesta que viene en camino, así que esa respuesta ya no manda. Sin esto
  // el guard de `loadResource` la seguía dando por buena y pisaba la escritura
  // — p. ej. el `getGroups()` que lanzó la precarga del rail al pasar el
  // puntero, resolviendo después de que el usuario creara un grupo y borrándolo
  // de la tabla.
  entry.promise = null;
  notify(entry);
}

/**
 * Marca claves como stale sin borrar el dato: quien esté montado lo sigue
 * viendo mientras se revalida, y quien monte después irá a la red.
 *
 * Cada argumento es clave exacta O prefijo, y se acepta una lista, para que un
 * cambio con efectos cruzados se declare en una línea: borrar un vehículo
 * cambia también la columna «Vehículo» de Dispositivos, así que invalidar solo
 * `fleet:vehicles` sería una verdad a medias.
 *
 * @param {string | string[]} prefixes — clave exacta o prefijo (o lista).
 */
export function invalidateResource(prefixes) {
  const list = Array.isArray(prefixes) ? prefixes : [prefixes];
  for (const entry of cache.values()) {
    const hit = list.some(
      (prefix) => entry.key === prefix || entry.key.startsWith(prefix),
    );
    if (!hit) continue;
    entry.fetchedAt = 0;
    notify(entry);
  }
}

/**
 * Vacía la caché conservando las suscripciones. Se llama en logout: los datos
 * de un usuario NO pueden sobrevivir al siguiente que entre en la misma
 * pestaña. Borrar el Map entero desconectaría a los componentes montados de
 * su entrada, así que se resetea en sitio.
 */
export function clearResourceCache() {
  for (const entry of cache.values()) {
    entry.data = undefined;
    entry.error = null;
    entry.fetchedAt = 0;
    entry.promise = null;
    notify(entry);
  }
}

/** Solo para tests: descarta entradas y suscripciones. */
export function __resetResourceCache() {
  cache.clear();
}
